import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  UseQueryResult,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback } from "react";
import { useApi } from "@/core/api";
import { normalizePhoneWithPlusPrefix } from "@/core/utils/phone";
import type {
  ICustomer,
  CustomerUpdateParams,
  CustomerWithPayouts,
  CustomerWithReferralsAndPayouts,
  INote,
} from "@/customer/customer";
import { payoutsKey } from "@/payouts/query";
import { referralsKey, useGetReferrals } from "@/referrals/query";
import {
  businessTextMessagesKey,
  customerTextMessagesKey,
} from "@/text-messages/query";
import { CUSTOMER_STATE } from "./constants";
import { getEnrichedCustomer } from "./utils";

export const customerKey = (customerId?: string | null) =>
  ["customer", customerId] as const;
export const customersKey = (businessId?: string | null, withPayouts = false) =>
  ["customers", businessId, withPayouts] as const;
export const customersV2BaseKey = (businessId?: string | null) =>
  ["customers-v2", businessId] as const;
export const customersV2Key = (
  businessId?: string | null,
  {
    customerType = "all",
    sortBy = "created_at",
    sortDirection = "desc",
    search = "",
    pageSize = 50,
    withPayouts = false,
    withReferrals = false,
  }: {
    customerType?: "all" | "leads" | "advocates";
    sortBy?: "created_at" | "name" | "referrals_count" | "payouts_total";
    sortDirection?: "asc" | "desc";
    search?: string;
    pageSize?: number;
    withPayouts?: boolean;
    withReferrals?: boolean;
  } = {},
) =>
  [
    ...customersV2BaseKey(businessId),
    customerType,
    sortBy,
    sortDirection,
    search,
    pageSize,
    withPayouts,
    withReferrals,
  ] as const;
export const customerNotesKey = (customerId: string) =>
  ["customer-notes", customerId] as const;

export interface CustomersV2Counts {
  all: number;
  leads: number;
  advocates: number;
}

export interface CustomersV2Response<TItem = ICustomer> {
  items: TItem[];
  counts: CustomersV2Counts;
  has_more: boolean;
  next_page: number | null;
}

interface UseGetCustomersV2Config {
  customerType?: "all" | "leads" | "advocates";
  enabled?: boolean;
  pageSize?: number;
  search?: string;
  sortBy?: "created_at" | "name" | "referrals_count" | "payouts_total";
  sortDirection?: "asc" | "desc";
  withPayouts?: boolean;
  withReferrals?: boolean;
}

export const invalidateCustomerQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  businessId?: string | null,
) => {
  queryClient.invalidateQueries({ queryKey: customersKey(businessId) });
  queryClient.invalidateQueries({ queryKey: customersV2BaseKey(businessId) });
};

export const useGetCustomer = (customerId?: string | null) => {
  const { get } = useApi();

  return useQuery({
    queryKey: customerKey(customerId),
    queryFn: () =>
      get(`/customers/${customerId}`).then(
        (res) => getEnrichedCustomer(res.data) as ICustomer,
      ),
    enabled: !!customerId,
  });
};

export const useGetAdvocate = (
  advocateId?: string | null,
): UseQueryResult<CustomerWithReferralsAndPayouts> => {
  const { get } = useApi();

  return useQuery({
    queryKey: ["advocate", advocateId] as const,
    queryFn: () =>
      get(`/customers/${advocateId}`).then(
        (res) =>
          getEnrichedCustomer(res.data) as CustomerWithReferralsAndPayouts,
      ),
    enabled: !!advocateId,
  });
};

interface useGetCustomersConfig {
  enabled?: boolean;
  withPayouts?: boolean;
}

export const useGetCustomers = <TData = ICustomer[]>(
  businessId?: string | null,
  select?: (data: TData) => TData,
  { enabled = true, withPayouts = false }: useGetCustomersConfig = {},
) => {
  const api = useApi();

  return useQuery({
    queryKey: customersKey(businessId, withPayouts),
    queryFn: async () => {
      const params = new URLSearchParams({
        business_id: businessId || "",
        exclude_states: CUSTOMER_STATE.opted_out,
      });
      if (withPayouts) {
        params.append("with_payouts", "true");
      }
      return api
        .get("/customers", { params })
        .then((res) => res.data.map(getEnrichedCustomer));
    },
    enabled: !!businessId && enabled,
    select,
  });
};

export const useGetCustomersV2 = <TItem extends ICustomer = ICustomer>(
  businessId?: string | null,
  {
    customerType = "all",
    enabled = true,
    pageSize = 50,
    search = "",
    sortBy = "created_at",
    sortDirection = "desc",
    withPayouts = false,
    withReferrals = false,
  }: UseGetCustomersV2Config = {},
) => {
  const api = useApi();
  const trimmedSearch = search.trim();

  return useInfiniteQuery({
    queryKey: customersV2Key(businessId, {
      customerType,
      sortBy,
      sortDirection,
      search: trimmedSearch,
      pageSize,
      withPayouts,
      withReferrals,
    }),
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({
        business_id: businessId || "",
        customer_type: customerType,
        exclude_states: CUSTOMER_STATE.opted_out,
        page: String(pageParam),
        page_size: String(pageSize),
        sort_by: sortBy,
        sort_direction: sortDirection,
      });

      if (trimmedSearch) {
        params.set("search", trimmedSearch);
      }

      if (withPayouts) {
        params.set("with_payouts", "true");
      }

      if (withReferrals) {
        params.set("with_referrals", "true");
      }

      return api
        .get("/v2/customers", { params })
        .then((res) => res.data as CustomersV2Response<TItem>)
        .then((data) => ({
          ...data,
          items: data.items.map(
            (customer) => getEnrichedCustomer(customer) as TItem,
          ),
        }));
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.next_page : undefined,
    enabled: !!businessId && enabled,
  });
};

export const useGetCustomersWithReferralsAndPayouts = (
  businessId?: string | null,
): UseQueryResult<CustomerWithReferralsAndPayouts[]> => {
  const {
    data: referralsData,
    isSuccess: isReferralsSuccess,
    isLoading: isReferralsLoading,
  } = useGetReferrals(businessId);

  const customersQuery = useGetCustomers(
    businessId,
    useCallback(
      (customers: CustomerWithPayouts[]): CustomerWithReferralsAndPayouts[] =>
        customers.map((customer) => ({
          ...customer,
          referrals:
            referralsData?.filter(
              (referral) => referral.advocate?.id === customer.id,
            ) || [],
          lead_referral: referralsData?.find(
            (referral) => referral.potential_customer?.id === customer.id,
          ),
        })),
      [referralsData],
    ),
    { enabled: isReferralsSuccess, withPayouts: true },
  );

  return {
    ...customersQuery,
    isLoading: customersQuery.isLoading || isReferralsLoading,
  } as UseQueryResult<CustomerWithReferralsAndPayouts[]>;
};

export const useGetCustomerNotes = (
  customerId: string,
): UseQueryResult<INote[]> => {
  const { get } = useApi();

  return useQuery<INote[]>({
    queryKey: customerNotesKey(customerId),
    queryFn: async () => {
      const response = await get(`/customers/${customerId}/notes`);
      return response.data;
    },
    enabled: !!customerId,
  });
};

interface AddCustomerNoteParams {
  customerId: string;
  content: string;
}

export const useCreateCustomerNote = () => {
  const { post } = useApi();
  const queryClient = useQueryClient();

  return useMutation<INote, Error, { customerId: string; content: string }>({
    mutationFn: ({ customerId, content }: AddCustomerNoteParams) =>
      post(`/customers/${customerId}/notes`, { note: { content } }).then(
        (response) => response.data as INote,
      ),
    onSuccess: (note, variables) => {
      queryClient.setQueryData<INote[]>(
        customerNotesKey(variables.customerId),
        (oldData = []) => [...oldData, note],
      );
    },
  });
};

interface DeleteCustomerNoteParams {
  customerId: string;
  noteId: string;
}

export const useDeleteCustomerNote = () => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ customerId, noteId }: DeleteCustomerNoteParams) =>
      api.delete(`/customers/${customerId}/notes/${noteId}`),
    onMutate: async ({ customerId, noteId }) => {
      await queryClient.cancelQueries({
        queryKey: customerNotesKey(customerId),
      });
      const previousNotes = queryClient.getQueryData<INote[]>(
        customerNotesKey(customerId),
      );

      queryClient.setQueryData<INote[]>(
        customerNotesKey(customerId),
        (old = []) => old.filter((note) => note.id !== noteId),
      );

      return { previousNotes };
    },
    onError: (_err, variables, context) => {
      if (context?.previousNotes) {
        queryClient.setQueryData<INote[]>(
          customerNotesKey(variables.customerId),
          context.previousNotes,
        );
      }
    },
  });
};

export const useUpsertCustomer = (businessId?: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { data: referralsData } = useGetReferrals(businessId);

  return useMutation({
    mutationFn: (customerUpdate: CustomerUpdateParams) => {
      const preparedCustomerUpdate = {
        ...customerUpdate,
        phone_number: normalizePhoneWithPlusPrefix(
          customerUpdate.phone_number || "",
        ),
      };

      return (
        customerUpdate.id
          ? api.put(`/customers/${customerUpdate.id}`, preparedCustomerUpdate)
          : api.post("/customers", {
              ...preparedCustomerUpdate,
              business_id: businessId,
              onboard: customerUpdate.onboard || undefined,
            })
      ).then((response) => response?.data);
    },
    onSuccess: (data, variables) => {
      // Update all relevant query caches
      const queryKeys = [
        customersKey(businessId, false), // without payouts
        customersKey(businessId, true), // with payouts
      ];

      // Add new customer
      const newCustomer = getEnrichedCustomer(data);
      const lead_referral = referralsData?.find(
        (referral) => referral.potential_customer?.id === newCustomer.id,
      );

      queryKeys.forEach((queryKey) => {
        queryClient.setQueryData<ICustomer[]>(queryKey, (oldData) => {
          if (!oldData) return oldData;

          if (variables.id) {
            // Update existing customer
            return oldData.map((customer) =>
              customer.id === variables.id
                ? {
                    ...(customer as ICustomer),
                    ...newCustomer,
                    lead_referral,
                  }
                : customer,
            );
          } else {
            // Add empty payouts array if this is the withPayouts query
            if (queryKey[2]) {
              return [
                ...oldData,
                {
                  ...(newCustomer as ICustomer),
                  payouts: [],
                  lead_referral,
                },
              ];
            }
            return [
              ...oldData,
              {
                ...(newCustomer as ICustomer),
                lead_referral,
              },
            ];
          }
        });
      });

      invalidateCustomerQueries(queryClient, businessId);
      queryClient.invalidateQueries({ queryKey: referralsKey(businessId) });
      queryClient.invalidateQueries({ queryKey: payoutsKey(businessId) });
    },
  });
};

export const useOptOutCustomer = (businessId?: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (customerId: string) =>
      api.put(`/customers/${customerId}`, {
        state: CUSTOMER_STATE.opted_out,
      }),
    onSuccess: (_, customerId) => {
      invalidateCustomerQueries(queryClient, businessId);
      queryClient.invalidateQueries({ queryKey: customerKey(customerId) });
      queryClient.invalidateQueries({
        queryKey: businessTextMessagesKey(businessId),
      });
      queryClient.invalidateQueries({
        queryKey: customerTextMessagesKey(customerId),
      });
    },
  });
};
